import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  ButtonGroup,
  DisplayText,
  IconLink,
  TextField,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { DeliveryPreferenceType } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

export const DeliveryPreferencesForm = ({
  preferences,
  submitting,
  onClose,
  onSave,
}: DeliveryPreferencesFormProps) => {
  const { match } = useResponsive();

  const [localType, setLocalType] = useState<string>(preferences.type ?? '');
  const [localNotes, setLocalNotes] = useState<string>(preferences.notes ?? '');

  const submitForm = useCallback(() => {
    onSave({
      type: localType as DeliveryPreferenceType,
      notes: localNotes,
    });
  }, [localNotes, localType, onSave]);

  return (
    <SafeAreaView style={match([styles.formXs, styles.formSm])}>
      <Header onClose={onClose} />
      <TypeSelector localType={localType} setLocalType={setLocalType} />
      <NotesField localNotes={localNotes} setLocalNotes={setLocalNotes} />
      <SubmitButton submitting={submitting} submitForm={submitForm} />
    </SafeAreaView>
  );
};

const Header = ({ onClose }: Partial<DeliveryPreferencesFormProps>) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const { isXS } = currentBreakpoint;

  return (
    <View style={isXS ? styles.column : styles.row}>
      {!isXS && (
        <DisplayText size={3}>{t('delivery.preferences.title')}</DisplayText>
      )}
      <View style={styles.icon}>
        <IconLink
          testID="delivery.preferences.close-button"
          name={isXS ? 'IconArrowLeft' : 'IconClose'}
          iconSize={24}
          accessibilityRole="button"
          accessibilityLabel={t('general.close')}
          accessibilityHint={t('general.close')}
          onPress={onClose}
        />
      </View>
      {isXS ? (
        <DisplayText style={styles.header} size={3}>
          {t('delivery.preferences.title')}
        </DisplayText>
      ) : null}
    </View>
  );
};

const TypeSelector = ({
  localType,
  setLocalType,
}: Readonly<{
  localType: string;
  setLocalType: (type: string) => void;
}>) => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();
  const itemsHorizontalPadding = match(['3', '4'] as const);

  const deliveryPreferences = useMemo(
    () => [
      {
        value: DeliveryPreferenceType.LeaveAtDoor,
        label: t('addresses.delivery-preference.leave-at-door'),
        accessibilityLabel: t(
          'addresses.delivery-preference.leave-at-door.a11y',
        ),
      },
      {
        value: DeliveryPreferenceType.Door,
        label: t('addresses.delivery-preference.meet-at-door'),
        accessibilityLabel: t(
          'addresses.delivery-preference.meet-at-door.a11y',
        ),
      },
      {
        value: DeliveryPreferenceType.Curbside,
        label: t('addresses.delivery-preference.meet-outside'),
        accessibilityLabel: t(
          'addresses.delivery-preference.meet-outside.a11y',
        ),
      },
    ],
    [t],
  );

  return (
    <View>
      <BodyText size={5} style={styles.sectionLabel}>
        {t('delivery.preferences.dropoff-label')}
      </BodyText>

      <ButtonGroup
        value={localType}
        items={deliveryPreferences}
        itemsHorizontalPadding={itemsHorizontalPadding}
        onChange={setLocalType}
      />
    </View>
  );
};

const NotesField = ({
  localNotes,
  setLocalNotes,
}: Readonly<{
  localNotes: string;
  setLocalNotes: (notes: string) => void;
}>) => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.flex}>
      <View style={[styles.row, styles.sectionLabel]}>
        <BodyText size={5}>{t('delivery.preferences.note-label')}</BodyText>
        <BodyText
          size={5}
          style={localNotes.length === 128 && styles.cautionText}
        >
          {localNotes.length}/{128}
        </BodyText>
      </View>
      <TextField
        testID="delivery.preferences.notes"
        style={styles.inputField}
        value={localNotes}
        multiline
        maxLength={128}
        placeholder={t('delivery.preferences.note-placeholder')}
        accessibilityLabel={t('delivery.preferences.note-hint')}
        accessibilityHint={t('delivery.preferences.note-hint')}
        onChangeText={setLocalNotes}
      />
    </View>
  );
};

const SubmitButton = ({ submitting, submitForm }: SubmitButtonProps) => {
  const { t } = useLocalizationContext();

  return (
    <Button
      size="large"
      width="100%"
      onPress={submitForm}
      isLoading={submitting}
      testID="delivery.preferences.save-button"
      accessibilityLabel={t('delivery.preferences.save.a11y')}
      accessibilityHint={t('delivery.preferences.save.a11y')}
      accessibilityRole="button"
    >
      {t('delivery.preferences.save')}
    </Button>
  );
};

type SubmitButtonProps = Readonly<{
  submitting?: boolean;
  submitForm: () => void;
}>;

type DeliveryPreferences = Readonly<{
  type: DeliveryPreferenceType;
  notes?: string;
}>;

type DeliveryPreferencesFormProps = Readonly<{
  preferences: DeliveryPreferences;
  submitting?: boolean;
  onClose: () => void;
  onSave: (preferences: DeliveryPreferences) => void;
}>;

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formSm: {
    flex: 1,
    padding: theme.spacing['6'],
  },
  formXs: {
    flex: 1,
    padding: theme.spacing['4'],
  },
  icon: {
    width: 24,
    height: 24,
  },
  header: {
    marginTop: theme.spacing['6'],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  column: {
    flexDirection: 'column',
  },
  sectionLabel: {
    marginTop: theme.spacing['10'],
    marginBottom: theme.spacing['2'],
  },
  inputField: {
    height: 80,
  },
  cautionText: {
    color: theme.colors.CAUTION,
  },
});
